import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { MessageCircle, Upload, X, ChevronRight, ChevronLeft, Camera, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";
import { trackEvent } from "@/utils/tracking";
import { trackEvent as internalTrack } from "@/utils/trackingClient";
import { openWhatsApp } from "@/utils/phone";
import { getAttributionPayload } from "@/utils/attribution";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const projectTypes = [
  "Badsanierung", "Trockenbau", "Verputzer", "Malerarbeiten", "Fliesenarbeiten",
  "Innenausbau", "Renovierung", "Komplettsanierung", "Sonstiges"
];

const accessOptions = [
  "Direkt vor Ort parken möglich", "Kurzer Fußweg", "Treppenhaus vorhanden",
  "Aufzug vorhanden", "Enge Zufahrt", "Kein Parkplatz direkt vor Ort",
  "Material muss weiter getragen werden", "Weiß ich noch nicht"
];

const floorOptions = [
  "Erdgeschoss", "1. Etage", "2. Etage", "3. Etage oder höher",
  "Keller", "Dachgeschoss"
];

const timelineOptions = [
  "So schnell wie möglich", "In 2–4 Wochen", "In 1–3 Monaten", "Ich plane erst noch"
];

const TOTAL_STEPS = 7;

const ProjectForm = ({ settings }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [formStarted, setFormStarted] = useState(false);
  const [formData, setFormData] = useState({
    project_type: "", description: "", photos: [],
    site_access: [], floor: "", timeline: "",
    name: "", phone: "", email: "", location: "",
    dsgvo_consent: false
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const fileRef = useRef(null);

  // UTM params
  const [utmParams, setUtmParams] = useState({});
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach(k => {
      if (params.get(k)) utm[k] = params.get(k);
    });
    setUtmParams(utm);
  }, []);

  // Track form start
  useEffect(() => {
    if (step > 0 && !formStarted) {
      setFormStarted(true);
      trackEvent("form_started", { form_type: "project" });
      // Funnel-Event ans eigene Backend (für /admin/analytics KPI „Formular geöffnet")
      internalTrack("form_open", { event_type: "click", extra: { form_type: "project" } });
    }
  }, [step, formStarted]);

  const handleFileUpload = useCallback(async (files) => {
    const ALLOWED_EXT = ["jpg", "jpeg", "png", "webp", "heic", "heif", "pdf"];
    const MAX_BYTES = 15 * 1024 * 1024;
    setUploading(true);

    const newPhotos = [];
    for (const file of Array.from(files).slice(0, 10 - formData.photos.length)) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(`${file.name}: Dateityp .${ext} nicht erlaubt. Erlaubt: JPG, PNG, WEBP, HEIC, PDF`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: Datei zu groß (max 15 MB)`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newPhotos.push({
          id: res.data.id,
          filename: file.name,
          preview: URL.createObjectURL(file),
          size: res.data.size
        });
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Unbekannter Fehler";
        toast.error(`${file.name}: ${msg}`);
      }
    }

    if (newPhotos.length > 0) {
      setFormData(prev => ({ ...prev, photos: [...prev.photos, ...newPhotos] }));
      trackEvent("photos_uploaded", { count: newPhotos.length });
      toast.success(`${newPhotos.length} Foto${newPhotos.length > 1 ? "s" : ""} erfolgreich hochgeladen`);
    }
    setUploading(false);
  }, [formData.photos.length]);

  const removePhoto = (photoId) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((p) => p.id !== photoId),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.phone || !formData.dsgvo_consent) {
      toast.error("Bitte füllen Sie die Pflichtfelder aus und stimmen Sie der Datenschutzerklärung zu.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        email: formData.email || null,
        photos: formData.photos.map(p => ({ id: p.id, filename: p.filename })),
        ...utmParams,
        device_type: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop",
        tracking: getAttributionPayload(),
      };
      const res = await api.post("/leads/project", payload);
      // Formular-Conversion feuert erst auf der Danke-Seite (lead_pending-Sperre)
      try {
        sessionStorage.setItem('lead_pending', JSON.stringify({
          gewerk: null,
          service: formData.project_type,
          transaction_id: res.data.id,
          phone: formData.phone,
          email: formData.email || null,
          ts: Date.now(),
        }));
      } catch (err) { /* sessionStorage nicht verfügbar */ }
      trackEvent("generate_lead", {
        service: formData.project_type,
        lead_name: formData.name,
        lead_id: res.data.id,
        source: "Projektformular",
        has_photos: formData.photos.length > 0
      });
      // Funnel-Event ans eigene Backend (KPI „Formular gesendet")
      internalTrack("form_submit", {
        event_type: "click",
        extra: { form_type: "project", lead_id: res.data.id, service: formData.project_type },
      });

      navigate("/anfrage-erfolgreich", {
        state: {
          lead_id: res.data.id,
          service: formData.project_type,
          source: "Projektformular",
          email: formData.email,
          has_photos: formData.photos.length > 0,
        },
      });
    } catch (e) {
      toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  const openWhatsAppLink = () => {
    const num = settings?.whatsapp_number || "4917620283482";
    let msg = "Hallo BEKAJ BAU,\nich möchte mein Projekt besprechen.\n\n";
    if (formData.project_type) msg += `Projektart: ${formData.project_type}\n`;
    if (formData.description) msg += `Beschreibung: ${formData.description}\n`;
    openWhatsApp(num, msg);
    trackEvent("contact_whatsapp", { source: "project_form" });
  };

  const canAdvance = () => {
    if (step === 1) return !!formData.project_type;
    if (step === 6) return !!formData.name && !!formData.phone && formData.dsgvo_consent;
    return true;
  };

  if (showSuccess) {
    return (
      <div className="text-center py-8" data-testid="project-form-success">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-9 h-9 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-[#4A331D] mb-2">Vielen Dank!</h3>
        <p className="text-gray-600 mb-6">Wir melden uns schnellstmöglich bei Ihnen.</p>
        <Button onClick={openWhatsAppLink} className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-6 py-4 text-base font-semibold rounded-lg">
          <MessageCircle className="w-5 h-5 mr-2" /> Direkt per WhatsApp kontaktieren
        </Button>
      </div>
    );
  }

  // Intro / Start screen
  if (step === 0) {
    return (
      <div className="text-center" data-testid="project-form-intro">
        <h3 className="text-xl font-bold text-[#4A331D] mb-2">Projekt beschreiben</h3>
        <p className="text-gray-500 text-sm mb-5">Je mehr wir vorab wissen, desto schneller können wir Ihnen eine realistische Einschätzung geben.</p>
        <Button
          onClick={() => setStep(1)}
          className="bg-[#D4AA6A] hover:bg-[#c49a5a] text-[#4A331D] px-8 py-5 text-base font-bold rounded-lg shadow-lg w-full"
          data-testid="start-project-form"
        >
          Projekt beschreiben <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="project-form">
      {/* Progress Bar */}
      <div className="mb-5">
        <div className="flex justify-between text-xs text-gray-400 mb-1">
          <span>Schritt {step} von {TOTAL_STEPS - 1}</span>
          <span>{Math.round((step / (TOTAL_STEPS - 1)) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div className="bg-[#D4AA6A] h-1.5 rounded-full transition-all duration-300" style={{ width: `${(step / (TOTAL_STEPS - 1)) * 100}%` }} />
        </div>
      </div>

      {/* Step 1: Project Type */}
      {step === 1 && (
        <div data-testid="step-project-type">
          <h3 className="font-bold text-[#4A331D] mb-3">Was soll gemacht werden?</h3>
          <div className="grid grid-cols-2 gap-2">
            {projectTypes.map(t => (
              <button
                key={t}
                onClick={() => {
                  setFormData(prev => ({ ...prev, project_type: t }));
                  trackEvent("project_type_selected", { type: t });
                }}
                className={`p-3 rounded-lg border text-sm font-medium transition-all text-left ${
                  formData.project_type === t
                    ? "border-[#D4AA6A] bg-[#D4AA6A]/10 text-[#4A331D]"
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
                data-testid={`type-${t.toLowerCase().replace(/\s/g, '-')}`}
              >
                {formData.project_type === t && <Check className="w-4 h-4 inline mr-1 text-[#D4AA6A]" />}
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Description */}
      {step === 2 && (
        <div data-testid="step-description">
          <h3 className="font-bold text-[#4A331D] mb-3">Beschreiben Sie kurz Ihr Projekt</h3>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Was soll gemacht werden? Z.B. Badezimmer komplett neu fliesen, ca. 12m²..."
            rows={4}
            className="border-gray-300 focus:border-[#D4AA6A]"
            data-testid="project-description"
          />
        </div>
      )}

      {/* Step 3: Photos */}
      {step === 3 && (
        <div data-testid="step-photos">
          <h3 className="font-bold text-[#4A331D] mb-1">Handyfotos hochladen</h3>
          <p className="text-gray-500 text-xs mb-3">Laden Sie 2–5 Fotos hoch. So können wir Ihr Projekt schneller einschätzen.</p>

          {formData.photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {formData.photos.map((photo) => (
                <div key={photo.id} className="relative rounded-lg overflow-hidden aspect-square">
                  <img src={photo.preview} alt={photo.filename} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removePhoto(photo.id)}
                    className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {formData.photos.length < 10 && (
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#D4AA6A] transition-colors"
              data-testid="photo-upload-btn"
            >
              {uploading ? (
                <p className="text-gray-500 text-sm">Wird hochgeladen...</p>
              ) : (
                <>
                  <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 font-medium">Fotos auswählen</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, HEIC, WebP, PDF • max. 15MB</p>
                </>
              )}
            </button>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/*,.pdf"
            multiple
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files)}
          />
        </div>
      )}

      {/* Step 4: Site Access */}
      {step === 4 && (
        <div data-testid="step-access">
          <h3 className="font-bold text-[#4A331D] mb-3">Wie gut kommt man an die Baustelle?</h3>
          <div className="space-y-2">
            {accessOptions.map(opt => {
              const selected = formData.site_access.includes(opt);
              return (
                <button
                  key={opt}
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      site_access: selected
                        ? prev.site_access.filter(a => a !== opt)
                        : [...prev.site_access, opt]
                    }));
                    trackEvent("site_access_selected", { option: opt });
                  }}
                  className={`w-full p-3 rounded-lg border text-sm text-left transition-all ${
                    selected
                      ? "border-[#D4AA6A] bg-[#D4AA6A]/10 text-[#4A331D]"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {selected && <Check className="w-4 h-4 inline mr-2 text-[#D4AA6A]" />}
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 5: Floor + Timeline */}
      {step === 5 && (
        <div className="space-y-5" data-testid="step-floor-timeline">
          <div>
            <h3 className="font-bold text-[#4A331D] mb-3">Welche Etage?</h3>
            <div className="grid grid-cols-2 gap-2">
              {floorOptions.map(f => (
                <button
                  key={f}
                  onClick={() => setFormData(prev => ({ ...prev, floor: f }))}
                  className={`p-3 rounded-lg border text-sm transition-all ${
                    formData.floor === f
                      ? "border-[#D4AA6A] bg-[#D4AA6A]/10 text-[#4A331D]"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-bold text-[#4A331D] mb-3">Wann soll das Projekt starten?</h3>
            <div className="grid grid-cols-1 gap-2">
              {timelineOptions.map(t => (
                <button
                  key={t}
                  onClick={() => setFormData(prev => ({ ...prev, timeline: t }))}
                  className={`p-3 rounded-lg border text-sm text-left transition-all ${
                    formData.timeline === t
                      ? "border-[#D4AA6A] bg-[#D4AA6A]/10 text-[#4A331D]"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {formData.timeline === t && <Check className="w-4 h-4 inline mr-2 text-[#D4AA6A]" />}
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 6: Contact */}
      {step === 6 && (
        <div className="space-y-4" data-testid="step-contact">
          <h3 className="font-bold text-[#4A331D] mb-1">Ihre Kontaktdaten</h3>
          <p className="text-gray-500 text-xs mb-2">Damit wir Ihnen eine Einschätzung geben können.</p>
          <div>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Vorname *"
              required
              className="h-12 border-gray-300"
              data-testid="project-name"
            />
          </div>
          <div>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="Telefonnummer *"
              type="tel"
              required
              className="h-12 border-gray-300"
              data-testid="project-phone"
            />
          </div>
          <div>
            <Input
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="E-Mail (optional)"
              type="email"
              className="h-12 border-gray-300"
              data-testid="project-email"
            />
          </div>
          <div>
            <Input
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              placeholder="Projektort (optional)"
              className="h-12 border-gray-300"
              data-testid="project-location"
            />
          </div>
          <div className="flex items-start gap-2 mt-3">
            <Checkbox
              id="dsgvo"
              checked={formData.dsgvo_consent}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, dsgvo_consent: checked }))}
              data-testid="dsgvo-checkbox"
            />
            <label htmlFor="dsgvo" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
              Ich stimme zu, dass meine Angaben und hochgeladenen Dateien zur Bearbeitung meiner Anfrage gespeichert werden.{" "}
              <a href="/datenschutz" target="_blank" className="text-[#D4AA6A] underline">Datenschutzerklärung</a>
            </label>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-3 mt-5">
        {step > 1 && (
          <Button
            variant="outline"
            onClick={() => setStep(s => s - 1)}
            className="flex-1 py-5"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
        )}
        {step < 6 ? (
          <Button
            onClick={() => setStep(s => s + 1)}
            disabled={!canAdvance()}
            className="flex-1 bg-[#D4AA6A] hover:bg-[#c49a5a] text-[#4A331D] py-5 font-semibold"
            data-testid="next-step-btn"
          >
            Weiter <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting || !canAdvance()}
            className="flex-1 bg-[#D4AA6A] hover:bg-[#c49a5a] text-[#4A331D] py-5 font-bold"
            data-testid="submit-project-btn"
          >
            {submitting ? "Wird gesendet..." : "Kostenlose Einschätzung anfordern"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProjectForm;
